import { tt } from './i18n/translation-memory'
import { parseMoneyToCents } from './money'

/**
 * *Chcete si priobjednať niečo navyše?* - what a product suggests once it is in the basket.
 *
 * **Here rather than in `customer-menu` since §9.657 (owner O44).** The owner asked for this prompt on the
 * waiter's screen too, and §5 forbids `manager-orders` importing a customer module - so the rules moved here and
 * `AddonPrompt` to `@agos/ui`, rather than a second copy of a function that puts money in a basket.
 * `agos-one-guard-spelled-twice`, on an upsell.
 *
 * **`addonGroups` is also the name of a `manager-menu` function, and that was checked before the export was
 * added.** That one takes `(products, productId, search)` and groups the *catalogue* for the picker a franchise
 * configures add-ons in; this one groups *resolved* add-ons for the prompt. No file imports both, and one that did
 * would be a redeclaration error rather than a silent shadow - §9.611's trap, met with the grep it prescribes.
 *
 * **Measured: live on this data.** `GET /products/6/addons` answers a *Cheddar dresing 50g* at 2 €, and
 * legacy asks after **every** add. This port had no concept of an add-on at all, which is a missing
 * upsell on the one screen a restaurant makes money from.
 *
 * **An add-on is an ordinary product.** It has its own id, price and VAT, and it goes in the basket as
 * itself - legacy adds it with `cart.addItem(addonProduct)` and so does this. That is why there is no
 * "add-on line" concept anywhere: the kitchen makes a dressing, the receipt prints a dressing, and the
 * only thing that is special is *when it was offered*.
 *
 * The offer is made **after** the product is in the basket, never before. Legacy adds first and asks
 * second, and the order is the point: a customer who dismisses the question still has what they asked
 * for, and one who never sees the question is not blocked from ordering.
 */

export interface Addon {
  id: number
  name: string
  priceCents: number
  description: string | null
  /** §9.70 - what the add-on is, for grouping. Null where the join names no category. */
  categoryName: string | null
  /** §9.70 - a picture where the menu has one; 10 of this product's 20 add-ons do. */
  imageUrl: string | null
}

export function addonsOf(payload: unknown): Addon[] {
  if (!Array.isArray(payload)) return []
  const addons: Addon[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    // Legacy's own filter: an add-on withdrawn from the menu is not offered, even though the join still
    // names it.
    if (row.active === false) continue
    /**
     * §9.232 - and one switched off is not offered either.
     *
     * §9.155 refuses to sell a product marked *Dočasne nedostupné*, but the prompt's confirm calls
     * `add({ productId })` directly and never reaches `addProduct`'s guard. The payload has carried
     * the flag since `V33`; this is the read that was missing.
     *
     * **Not offered rather than offered and refused**: the menu greys a card because a card is a
     * listing, and hiding it would read as a product that had vanished. A prompt is a suggestion,
     * and suggesting what cannot be bought is worse than not suggesting it.
     *
     * `!== false` keeps selling anything whose payload predates the field, exactly as `addProduct`
     * does.
     */
    if (row.orderable === false) continue
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    if (typeof row.id !== 'number' || name === '') continue
    addons.push({
      id: row.id,
      name,
      priceCents:
        typeof row.price === 'number' || typeof row.price === 'string'
          ? parseMoneyToCents(row.price)
          : 0,
      description:
        typeof row.description === 'string' && row.description.trim() !== '' && row.description.trim() !== name
          ? row.description.trim()
          : null,
      categoryName:
        typeof row.categoryName === 'string' && row.categoryName.trim() !== ''
          ? row.categoryName.trim()
          : null,
      imageUrl:
        typeof row.imageUrl === 'string' && row.imageUrl.trim() !== '' ? row.imageUrl.trim() : null,
    })
  }
  return addons
}

/** What the chosen add-ons come to, in cents - shown before the customer commits to them. */
export function addonsTotalCents(addons: readonly Addon[], chosen: ReadonlySet<number>): number {
  return addons.reduce((total, addon) => (chosen.has(addon.id) ? total + addon.priceCents : total), 0)
}

/** The button's own words, so *add nothing* reads as a choice rather than as a way out. */
export function addonsConfirmLabel(chosenCount: number): string {
  return chosenCount === 0 ? tt('Nie, ďakujem') : tt('Pridať do košíka')
}

/**
 * The add-ons in the groups a customer reads them in - `FRONTEND.md` §9.70.
 *
 * **Twenty in one column is the complaint.** Product 6 offers exactly that: 9 dressings, 5 sides,
 * 3 drinks and 3 extras, in one flat list where a dressing and a drink look like the same kind of
 * decision.
 *
 * Groups keep the order the server sent, and so do the add-ons inside them - `sort_order` is the
 * menu's own arrangement, and re-sorting it alphabetically would reshuffle a list somebody arranged.
 *
 * **Uncategorised go last, in one group with no heading.** They are not a category called *(žiadna)*;
 * they are the ones nobody filed, and inventing a name for them puts a word on the screen that is not
 * in the menu.
 */
export function addonGroups(addons: readonly Addon[]): { name: string | null; addons: Addon[] }[] {
  const groups: { name: string | null; addons: Addon[] }[] = []
  for (const addon of addons) {
    const existing = groups.find((group) => group.name === addon.categoryName)
    if (existing === undefined) groups.push({ name: addon.categoryName, addons: [addon] })
    else existing.addons.push(addon)
  }
  return [
    ...groups.filter((group) => group.name !== null),
    ...groups.filter((group) => group.name === null),
  ]
}
